/**
 * App Picker – home view for multi-app TraceOS deployments.
 * The list of visible apps is driven by config.customization.appPickerApps.
 * When that field is absent, all built-in apps are shown (internal / dev deployments).
 */

import React from 'react';
import { Card } from '../UI/Card';
import { getBaseUrl } from '../../utils/env';
import { useConfig } from '../../contexts/ConfigContext';
import { BarChart3, FileText, Database, Layers, Zap, Users } from 'lucide-react';
import { LandingHeroShell } from '../Landing/LandingHeroShell';
import { getLandingBranding } from '../Landing/landingBranding';

const APPS = [
  {
    id: 'baseline',
    path: '/baseline/',
    name: 'Baseline',
    icon: BarChart3,
    description: 'Clean slate deployment with all core TraceKit features. Perfect for general-purpose evidence management, RAG queries, and report generation.',
    features: ['RAG Chat', 'Entity Explorer', 'Reports', 'Sources'],
  },
  {
    id: 'minescope-india',
    path: '/minescope-india/',
    name: 'MineScope India',
    icon: Layers,
    description: 'Comprehensive mining compliance and operations platform for SPARC India. Features environmental monitoring, compliance tracking, LA management, and regulatory reporting.',
    features: ['Environmental Monitoring', 'Compliance Tracker', 'Mining Operations', 'Universal Search', '26,000+ Entities'],
  },
  {
    id: 'keystone-rfp',
    path: '/keystone-rfp/',
    name: 'Keystone RFP',
    icon: FileText,
    description: 'AI-powered RFP management and proposal generation platform. Streamline your bidding process with intelligent document analysis and automated proposal creation.',
    features: ['RFP Analysis', 'Proposal Generation', 'Document Management'],
  },
  {
    id: 'pfas-cfb-comox',
    path: '/pfas-cfb-comox/',
    name: 'PFAS CFB Comox',
    icon: Database,
    description: 'Environmental monitoring platform for PFAS contamination assessment at Canadian Forces Base Comox. Features TEA classification, temporal trend analysis, spatial mapping, and change detection.',
    features: ['TEA Classification', 'Trend Analysis', 'Spatial Mapping', 'Change Detection', '2,085+ Entities'],
  },
  {
    id: 'marpac-marine',
    path: '/marpac-marine/',
    name: 'MARPAC Marine',
    icon: Zap,
    description: 'Environmental impact assessment platform for Maritime Forces Pacific. Features training exercise tracking, munitions expenditure analysis, ecological observations, and scenario modeling.',
    features: ['Training Exercises', 'Munitions Tracking', 'Ecological Monitoring', 'Scenario Analysis', 'MIKE21 Models'],
  },
  {
    id: 'sparc-hr',
    path: '/sparc-hr/',
    name: 'HR Assistant',
    icon: Users,
    description: 'HR AI platform. Policy Intelligence, Leave & Attendance, Performance, Recruitment, and Governance—Wakib API integration.',
    features: ['Policy Intelligence', 'Leave & Attendance', 'Performance', 'Recruitment', 'Universal Search'],
  },
] as const;

function getAppHref(appId: string, path: string, baseUrl: string): string {
  if (import.meta.env.DEV) {
    const key = `VITE_APP_PICKER_${appId.toUpperCase().replace(/-/g, '_')}_URL`;
    const url = (import.meta.env as Record<string, string | undefined>)[key];
    if (url && typeof url === 'string') return url;
  }
  if (baseUrl === '/' || !baseUrl) return path;
  return baseUrl.replace(/\/$/, '') + (path.startsWith('/') ? path : `/${path}`);
}

export interface AppPickerProps {
  /** When set, show a Login/Get started button (for logged-out landing). */
  loginHref?: string;
}

export function AppPicker({ loginHref }: AppPickerProps = {}) {
  const config = useConfig();
  const baseUrl = getBaseUrl();
  const branding = getLandingBranding(config);
  const primaryColor = branding.primaryColor;

  // Filter apps by config; fall back to full list for internal/dev deployments
  const allowedAppIds = config?.customization?.appPickerApps;
  const visibleApps = allowedAppIds
    ? APPS.filter((app) => allowedAppIds.includes(app.id))
    : APPS;

  return (
    <div className="min-h-screen" style={{ fontFamily: "-apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif" }}>
      {loginHref && (
        <LandingHeroShell
          branding={branding}
          loginHref={loginHref}
          heading="Evidence-driven platform suite"
          gradientKeyword="platform"
          description="Select an application below or log in to continue. Powered by TraceOS."
          marqueeItems={visibleApps.map((app) => ({ label: app.name, icon: app.icon, key: app.id }))}
          showAbstractDashboard
          heroVariant={config?.theme?.allowToggle === false && config?.theme?.defaultMode === 'light' ? 'light' : 'dark'}
        />
      )}

      {!loginHref && (
        <div className="bg-[#030303] py-16 px-6">
          <div className="mx-auto max-w-4xl text-center">
            <h1 className="text-3xl font-bold text-white md:text-4xl">{branding.appName}</h1>
            <p className="mt-4 text-lg text-gray-300">
              Evidence-driven platform suite powered by TraceOS.
            </p>
          </div>
        </div>
      )}

      <div className="bg-gradient-to-b from-white/95 to-gray-50 px-6 py-12">
        <div className="mx-auto max-w-4xl space-y-6">
          {visibleApps.map((app) => {
            const Icon = app.icon;
            const href = getAppHref(app.id, app.path, baseUrl);
            return (
              <a
                key={app.id}
                href={href}
                className="block focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 rounded-xl transition-all duration-200 focus-visible:ring-blue-500"
              >
                <Card
                  className="group relative overflow-hidden border-2 p-6 transition-all duration-200 hover:shadow-xl"
                  style={{
                    borderColor: 'rgba(0,0,0,0.08)',
                    boxShadow: '0 0 0 1px rgba(0,0,0,0.04)',
                  }}
                >
                  <div
                    className="absolute inset-0 opacity-0 transition-opacity group-hover:opacity-100"
                    style={{
                      background: `linear-gradient(135deg, ${primaryColor}08 0%, transparent 50%)`,
                    }}
                  />
                  <div className="relative flex items-start gap-4">
                    <div
                      className="flex h-14 w-14 flex-shrink-0 items-center justify-center rounded-xl text-white transition-transform group-hover:scale-105"
                      style={{
                        background: `linear-gradient(135deg, ${primaryColor}, ${branding.secondaryColor})`,
                      }}
                    >
                      <Icon className="h-7 w-7" />
                    </div>
                    <div className="min-w-0 flex-1">
                      <div className="mb-1 flex items-center justify-between gap-2">
                        <h2 className="text-xl font-semibold text-gray-900">{app.name}</h2>
                        <span className="flex-shrink-0 rounded-full border px-2.5 py-0.5 text-xs font-medium text-green-700 border-green-200 bg-green-50">
                          Active
                        </span>
                      </div>
                      <p className="mb-3 text-sm text-gray-600">{app.description}</p>
                      <div className="flex flex-wrap gap-2">
                        {app.features.map((f) => (
                          <span
                            key={f}
                            className="rounded-md bg-gray-100 px-2.5 py-1 text-xs font-medium text-gray-600"
                          >
                            {f}
                          </span>
                        ))}
                      </div>
                    </div>
                    <span
                      className="flex-shrink-0 text-gray-400 transition-colors group-hover:text-gray-600"
                      style={{ color: primaryColor }}
                    >
                      →
                    </span>
                  </div>
                </Card>
              </a>
            );
          })}
        </div>

        <div className="mx-auto mt-12 max-w-4xl border-t border-gray-200 pt-6 text-center">
          <p className="text-sm text-gray-500">
            TraceOS • Built with care by{" "}
            <a
              href="https://alpyne.ai"
              target="_blank"
              rel="noopener noreferrer"
              className="font-medium hover:underline"
              style={{ color: primaryColor }}
            >
              Alpyne AI
            </a>
          </p>
        </div>
      </div>
    </div>
  );
}
